import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sportingService, type CountryStatistics } from '../../services/sportingService';
import {
  ArrowLeft, Users, Calendar, TrendingUp, Search,
  Filter, Award, Building2, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import PlayerCard from '../../components/Sporting/PlayerCard';
import PlayerListItem from '../../components/Sporting/PlayerListItem';
import ViewControls, { type ViewMode, type ItemsPerPage } from '../../components/Sporting/ViewControls';

// Usar el mismo mapeo de códigos de país del CountryExplorerPage
const countryToCode: Record<string, string> = {
  'Spain': 'es', 'England': 'gb-eng', 'France': 'fr', 'Germany': 'de', 'Italy': 'it',
  'Portugal': 'pt', 'Brazil': 'br', 'Argentina': 'ar', 'Netherlands': 'nl', 'Belgium': 'be',
  'Croatia': 'hr', 'Uruguay': 'uy', 'Colombia': 'co', 'Mexico': 'mx', 'Chile': 'cl',
  'Poland': 'pl', 'Austria': 'at', 'Switzerland': 'ch', 'Denmark': 'dk', 'Sweden': 'se',
  'Norway': 'no', 'Czech Republic': 'cz', 'Serbia': 'rs', 'Turkey': 'tr', 'Greece': 'gr',
  'Japan': 'jp', 'South Korea': 'kr', 'Australia': 'au', 'United States': 'us', 'Canada': 'ca',
  'Nigeria': 'ng', 'Senegal': 'sn', 'Ghana': 'gh', 'Cameroon': 'cm', 'Morocco': 'ma',
  'Egypt': 'eg', 'Algeria': 'dz', 'Tunisia': 'tn', 'Ivory Coast': 'ci', 'South Africa': 'za',
  'Scotland': 'gb-sct', 'Wales': 'gb-wls', 'Northern Ireland': 'gb-nir', 'Republic of Ireland': 'ie',
  'Russia': 'ru', 'Ukraine': 'ua', 'Romania': 'ro', 'Hungary': 'hu', 'Slovakia': 'sk',
  'Bosnia-Herzegovina': 'ba', 'Slovenia': 'si', 'Albania': 'al', 'North Macedonia': 'mk',
  'Ecuador': 'ec', 'Paraguay': 'py', 'Peru': 'pe', 'Venezuela': 've', 'Bolivia': 'bo',
  'Costa Rica': 'cr', 'Panama': 'pa', 'Jamaica': 'jm', 'Honduras': 'hn', 'El Salvador': 'sv',
  'China PR': 'cn', 'Iran': 'ir', 'Saudi Arabia': 'sa', 'Qatar': 'qa', 'United Arab Emirates': 'ae',
  'Israel': 'il', 'India': 'in', 'Thailand': 'th', 'Vietnam': 'vn', 'Indonesia': 'id',
  'Kosovo': 'xk', 'DR Congo': 'cd', 'Congo': 'cg', 'Türkiye': 'tr', 'Hong Kong': 'hk',
  'Equatorial Guinea': 'gq', 'Kazakhstan': 'kz', 'Central African Republic': 'cf',
  'Zimbabwe': 'zw', 'Lesotho': 'ls', 'North Korea': 'kp',
};

const CountryPlayersPage: React.FC = () => {
  const { country } = useParams<{ country: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [players, setPlayers] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<CountryStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [sortBy, setSortBy] = useState('player_name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [includeRetired, setIncludeRetired] = useState(false);

  // View controls state
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('playerViewMode');
    return (saved as ViewMode) || 'grid';
  });
  const [itemsPerPage, setItemsPerPage] = useState<ItemsPerPage>(() => {
    const saved = localStorage.getItem('playerItemsPerPage');
    return (saved ? Number(saved) as ItemsPerPage : 24);
  });

  // Save view preferences to localStorage
  useEffect(() => {
    localStorage.setItem('playerViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('playerItemsPerPage', itemsPerPage.toString());
  }, [itemsPerPage]);

  useEffect(() => {
    if (country) {
      loadPlayers();
      loadStatistics();
    }
  }, [country, page, positionFilter, sortBy, sortOrder, includeRetired, itemsPerPage]);

  const loadPlayers = async () => {
    try {
      setLoading(true);
      const data = await sportingService.getPlayersByCountry(
        country!,
        page,
        itemsPerPage,
        positionFilter,
        sortBy,
        sortOrder,
        includeRetired
      );
      setPlayers(data.players);
      setTotalPages(data.total_pages);
    } catch (error: any) {
      toast.error(error.message || t('errors.generic'));
      console.error('Error loading players:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      setLoadingStats(true);
      const stats = await sportingService.getCountryStatistics(country!);
      setStatistics(stats);
    } catch (error: any) {
      toast.error(error.message || t('errors.generic'));
      console.error('Error loading statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const getCountryCode = (countryName: string): string => {
    return countryToCode[countryName] || countryName.toLowerCase().substring(0, 2);
  };

  const filteredPlayers = players.filter(player =>
    !searchTerm || player.player_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSortChange = (newSortBy: string) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(newSortBy);
      setSortOrder('ASC');
    }
    setPage(1);
  };

  if (!country) {
    return null;
  }

  const countryCode = getCountryCode(country);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header with Flag */}
      <div className="relative bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 rounded-2xl shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 to-purple-800/90"></div>
        
        <div className="relative z-10 p-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/sporting/countries')}
            className="flex items-center space-x-2 text-white/90 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('countries.backToCountries')}</span>
          </button>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Flag */}
            <div className="w-32 h-24 rounded-xl overflow-hidden shadow-lg border-4 border-white/20">
              <img
                src={`https://flagcdn.com/w320/${countryCode}.png`}
                alt={country}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = `https://placehold.co/320x240/e5e7eb/6b7280?text=${encodeURIComponent(country)}`;
                }}                
              />
            </div>

            {/* Country Info */}
            <div className="flex-1 text-white">
              <h1 className="text-4xl font-bold mb-2">{country}</h1>
              <p className="text-blue-100 text-lg">
                {statistics ? `${statistics.total_players} ${t('countries.registeredPlayers')}` : t('countries.loading')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && !loadingStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">{statistics.total_players}</span>
            </div>
            <p className="text-sm text-gray-600">{t('countries.totalPlayers')}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 text-purple-600" />
              <span className="text-2xl font-bold text-gray-900">{statistics.avg_age}</span>
            </div>
            <p className="text-sm text-gray-600">{t('countries.avgAge')}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <span className="text-2xl font-bold text-gray-900">{statistics.players_with_contract}</span>
            </div>
            <p className="text-sm text-gray-600">{t('countries.withContract')}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Award className="w-8 h-8 text-orange-600" />
              <span className="text-2xl font-bold text-gray-900">{statistics.expiring_contracts}</span>
            </div>
            <p className="text-sm text-gray-600">{t('countries.expiringContracts')}</p>
          </div>
        </div>
      )}

      {/* Top Positions & Clubs */}
      {statistics && !loadingStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Top Positions */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <Award className="w-5 h-5 mr-2 text-blue-600" />
              {t('countries.topPositions')}
            </h3>
            <div className="space-y-2">
              {statistics.top_positions.slice(0, 5).map((pos, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setPositionFilter(pos.position);
                    setPage(1);
                    // Scroll to players section
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                  }}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 hover:border-blue-300 transition-all duration-200 group"
                  title={t('countries.clickToFilter')}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </div>
                    <span className="text-gray-800 font-medium group-hover:text-blue-900 transition-colors">{pos.position}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-blue-700 text-lg">{pos.count}</span>
                    <Filter className="w-4 h-4 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
            {positionFilter && (
              <button
                onClick={() => {
                  setPositionFilter('');
                  setPage(1);
                }}
                className="mt-4 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                <span className="text-sm font-medium">Limpiar filtro de posición</span>
              </button>
            )}
          </div>

          {/* Top Clubs */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-purple-600" />
              {t('countries.topClubs')}
            </h3>
            <div className="space-y-2">
              {statistics.top_clubs.slice(0, 5).map((club, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </div>
                    <span className="text-gray-800 font-medium truncate">{club.club}</span>
                  </div>
                  <span className="font-bold text-purple-700 text-lg">{club.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
        <div className="flex flex-col gap-4">
          {/* Search and Retired Toggle Row */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('countries.searchPlayer')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Retired Toggle */}
            <label className="flex items-center space-x-3 bg-gray-50 px-4 py-2 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={includeRetired}
                onChange={(e) => {
                  setIncludeRetired(e.target.checked);
                  setPage(1);
                }}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                {t('countries.includeRetired')}
              </span>
            </label>
          </div>

          {/* Position Filter and Sort Row */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Position Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={positionFilter}
                onChange={(e) => {
                  setPositionFilter(e.target.value);
                  setPage(1);
                }}
                className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer bg-white min-w-[180px]"
              >
                <option value="">{t('countries.allPositions')}</option>
                <option value="Portero">{t('countries.goalkeeper')}</option>
                <option value="Defensa">{t('countries.defender')}</option>
                <option value="Medio">{t('countries.midfielder')}</option>
                <option value="Delantero">{t('countries.forward')}</option>
              </select>
            </div>

            {/* Sort */}
            <div className="flex gap-2">
              <button
                onClick={() => handleSortChange('player_name')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  sortBy === 'player_name'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('countries.sortByName')}
              </button>
              <button
                onClick={() => handleSortChange('age')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  sortBy === 'age'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('countries.sortByAge')}
              </button>
            </div>
          </div>

          {/* Active filters indicator */}
          {(positionFilter || searchTerm) && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200">
              <span className="text-sm text-gray-600 font-medium">Filtros activos:</span>
              {positionFilter && (
                <button
                  onClick={() => {
                    setPositionFilter('');
                    setPage(1);
                  }}
                  className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors"
                >
                  <span>{positionFilter}</span>
                  <X className="w-3 h-3" />
                </button>
              )}
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors"
                >
                  <span>Búsqueda: "{searchTerm}"</span>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Controls and Players Display */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('countries.loadingPlayers')}</p>
          </div>
        </div>
      ) : filteredPlayers.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Users className="w-20 h-20 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('countries.noPlayersFound')}</h3>
          <p className="text-gray-600">{t('countries.tryDifferentFilters')}</p>
        </div>
      ) : (
        <>
          {/* View Controls */}
          <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
            <ViewControls
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(items) => {
                setItemsPerPage(items);
                setPage(1); // Reset to first page when changing items per page
              }}
              totalItems={filteredPlayers.length}
            />
          </div>

          {/* Players Display - Grid or List */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredPlayers.map((player) => (
                <PlayerCard
                  key={player.player_id}
                  player={player}
                  onViewProfile={(playerId) => navigate(`/sporting/player-detail/${playerId}`)}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPlayers.map((player) => (
                <PlayerListItem
                  key={player.player_id}
                  player={player}
                  onViewProfile={(playerId) => navigate(`/sporting/player-detail/${playerId}`)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('countries.previousPage')}
              </button>
              
              <span className="px-4 py-2 text-gray-700">
                {t('countries.page')} {page} {t('countries.of')} {totalPages}
              </span>
              
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('countries.nextPage')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CountryPlayersPage;
